/* eslint-disable new-cap */
import React, { useContext, useState } from 'react';
import I18n from 'i18n-js';
import { FetchResult } from '@apollo/client';
import { RCButton, RCRadioGroup } from '@reverbdotcom/cadence/components';
import {
  core_apimessages_ListingStateUpdate,
} from '@reverbdotcom/commons/src/rql';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import SellerListingCardContext from '../SellerListingCardContext';
import ModalDialog from '@reverbdotcom/commons/src/components/modal_dialog';
import { ActionType } from '../sellerListingCardReducer';
import refetchCardListing from '../refetchCardListing';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { parseAmountCents } from '@reverbdotcom/commons/src/money';

const DEFAULT_SELL_FEE_PERCENT = 0.05;
const ADMIN_SELL_FEE_PERCENT = 0;
interface IMutationError {
  extensions: {
    errors: [{
      message: string
    }]
  },
}

type IExtendedMutationResult<TData> = FetchResult<TData> & {
  errors?: IMutationError[]
};

export const COMPONENT_NAME = 'ListingRowEndButton';

function ListingRowEndButton({ updateListingState }) {
  const listingCardContext = useContext(
    SellerListingCardContext,
  );
  const [isModalOpen, setModalOpen] = useState(false);
  const [endListingReason, setEndListingReason] = useState('no_longer_available');
  const { cardState, dispatch } = useContext(
    SellerListingCardContext,
  );

  const user = useUser();
  const sellFeePercent = user.isAdmin ? ADMIN_SELL_FEE_PERCENT : DEFAULT_SELL_FEE_PERCENT;
  const feePercent = sellFeePercent * 100;
  const { sellerPrice } = listingCardContext.cardState.listing;
  const feeAmountDisplay = parseAmountCents(sellFeePercent * sellerPrice.amountCents, sellerPrice.currency).display;
  
  async function startEndListing() {
    dispatch({
      type: ActionType.SET_LOADING_LISTING,
      payload: {
        loadingListing: true,
      },
    });

    try {
      const res = await updateListingState({
        variables: {
          input: {
            productId: cardState.listing.id,
            newState: core_apimessages_ListingStateUpdate.ENDED,
            endingReason: endListingReason,
          },
        },
      }) as IExtendedMutationResult<typeof updateListingState>;

      const { hasErrors } = checkResponse(res);

      if (hasErrors) {
        const [userError] = formatErrors(res.errors);

        dispatch({
          type: ActionType.SET_LOADING_LISTING,
          payload: {
            loadingListing: false,
          },
        });

        dispatch({
          type: ActionType.SHOW_TOAST,
          payload: {
            isSuccess: false,
            message: userError?.message,
          },
        });
      } else {
        dispatch({
          type: ActionType.SHOW_TOAST,
          payload: {
            isSuccess: true,
            message: I18n.t(
              'discovery.listingsManagement.sellerListingCard.endSuccess',
            ),
          },
        });
        
        refetchCardListing(listingCardContext);
      }
    } catch (error) {
      dispatch({
        type: ActionType.SET_LOADING_LISTING,
        payload: {
          loadingListing: false,
        },
      });
      dispatch({
        type: ActionType.SHOW_TOAST,
        payload: {
          isSuccess: false,
          message: I18n.t(
            'discovery.listingsManagement.genericError',
          ),
        },
      });
    }
  }

  function checkResponse(
    res,
  ) {
    return {
      hasErrors: !!res.errors?.length,
      hasData: !!res.data,
    };
  }

  function formatErrors(errors) {
    return errors[0]?.extensions?.errors || [];
  }

  function handleEndListingReason(e: React.ChangeEvent<HTMLInputElement>) {
    setEndListingReason(e.target.value);
  }

  return (
    <>
      <RCButton
        onClick={() => {
          trackEvent({
            eventName: MParticleEventName.ClickedComponent,
            componentName: COMPONENT_NAME,
            listingId: cardState.listing.id,
          });
          setModalOpen(true);
        }
        }
        variant="outlined"
        fullWidth
      >
        {I18n.t('discovery.listingsManagement.sellerListingCard.end')}
      </RCButton>

      <ModalDialog
        headerTitle={I18n.t('discovery.listingsManagement.sellerListingCard.endListingModal.modalHeaderTitle')}
        isOpen={isModalOpen}
        onRequestClose={() => setModalOpen(false)}
        saveButtonText={I18n.t('discovery.listingsManagement.sellerListingCard.endListingModal.modalEndButton')}
        onSubmit={startEndListing}
      >
        <div className="g-container">
          <div className="size-n">
            {I18n.t('discovery.listingsManagement.sellerListingCard.endListingModal.modalTitle')}
          </div>
          <div>
            <RCRadioGroup
              id="endListingReasonGroup"
              label=""
              name="endListingReasonGroup"
              onChange={handleEndListingReason}
              value={endListingReason}
            >
              <RCRadioGroup.Option
                label={I18n.t('discovery.listingsManagement.sellerListingCard.endListingModal.soldOption')}
                value="no_longer_available"
              />
              <RCRadioGroup.Option
                label={I18n.t('discovery.listingsManagement.sellerListingCard.endListingModal.foundOption', {
                  feePercent, 
                  feeAmountDisplay,
                })}
                value="sold_via_messages"
              />
            </RCRadioGroup>
          </div>
        </div>
      </ModalDialog>
    </>
  );
}

export default ListingRowEndButton;
